import { Outlet, useNavigate } from 'react-router-dom'
import { Box, Flex, HStack, Heading, Text, Portal } from '@chakra-ui/react'
import { MenuRoot, MenuTrigger, MenuContent, MenuItem, MenuPositioner } from '@chakra-ui/react'
import {
  LayoutDashboard,
  Building2,
  Users,
  Home,
  Calendar,
  DollarSign,
  FileText,
  LogOut,
  Settings,
  User,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLogout, useAuth } from '@/features/auth/hooks/useAuth'
import { useSubscription } from '@/shared/hooks/useSubscription'
import { UpgradeBanner } from '@/shared/components/UpgradeBanner'
import { CollapsibleMainSidebar } from './CollapsibleMainSidebar'
import { Avatar } from '@/shared/components/Avatar'

export function DashboardLayout() {
  const { t, i18n } = useTranslation()
  const logout = useLogout()
  const navigate = useNavigate()
  const { data: auth } = useAuth()
  const { isPastDue, isCanceled } = useSubscription()

  const handleLogout = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/app/login'),
    })
  }

  const userName =
    auth?.profile?.first_name && auth?.profile?.last_name
      ? `${auth.profile.first_name} ${auth.profile.last_name}`
      : 'Mon compte'

  const navigationItems = [
    { path: '/app/dashboard', label: t('navigation.dashboard'), icon: LayoutDashboard, badge: 'TODO' },
    { path: '/app/properties', label: t('navigation.properties'), icon: Building2, badge: 'WIP' },
    { path: '/app/owners', label: t('navigation.owners'), icon: Users, badge: 'WIP' },
    { path: '/app/units', label: t('navigation.units'), icon: Home, badge: 'WIP' },
    { path: '/app/meetings', label: t('navigation.meetings'), icon: Calendar, badge: 'TODO' },
    { path: '/app/accounting', label: t('navigation.accounting'), icon: DollarSign, badge: 'TODO' },
    { path: '/app/documents', label: t('navigation.documents'), icon: FileText, badge: 'WIP' },
  ]

  return (
    <Flex h="100vh" bg="gray.50" overflow="hidden">
      {/* Sidebar */}
      <CollapsibleMainSidebar navigationItems={navigationItems} />

      {/* Main Content */}
      <Flex flex={1} direction="column" overflow="hidden">
        {/* Upgrade Banner si nécessaire */}
        {isPastDue && (
          <UpgradeBanner reason="payment_failed" />
        )}
        {isCanceled && (
          <UpgradeBanner reason="subscription_canceled" />
        )}

        {/* Top Bar */}
        <Box
          bg="white"
          px={8}
          py={4}
          borderBottom="1px solid"
          borderColor="gray.200"
          position="sticky"
          top={0}
          zIndex={10}
          boxShadow="sm"
        >
          <HStack justify="space-between">
            <Box>
              <Heading size="md" fontWeight="semibold">
                {t('auth.welcome')}
              </Heading>
              <Text fontSize="sm" color="gray.600">
                {new Date().toLocaleDateString(i18n.language, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Text>
            </Box>

            <HStack gap={3}>
              <MenuRoot positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
                <MenuTrigger asChild>
                  <HStack gap={3} cursor="pointer" _hover={{ opacity: 0.8 }} transition="opacity 0.2s">
                    <Box textAlign="right">
                      <Text fontSize="sm" fontWeight="medium" color="gray.800">
                        {userName}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {auth?.user?.email || ''}
                      </Text>
                    </Box>
                    <Avatar
                      size="sm"
                      name={userName}
                      bg="brand.500"
                    />
                  </HStack>
                </MenuTrigger>
                <Portal>
                  <MenuPositioner>
                    <MenuContent minW="200px">
                      <MenuItem
                        value="profile"
                        onClick={() => navigate('/app/settings/profile')}
                      >
                        <HStack gap={2}>
                          <User size={16} />
                          <Text>Mon compte</Text>
                        </HStack>
                      </MenuItem>
                      <MenuItem
                        value="settings"
                        onClick={() => navigate('/app/settings/agency')}
                      >
                        <HStack gap={2}>
                          <Settings size={16} />
                          <Text>Paramètres</Text>
                        </HStack>
                      </MenuItem>
                      <MenuItem
                        value="logout"
                        onClick={handleLogout}
                        color="red.600"
                      >
                        <HStack gap={2}>
                          <LogOut size={16} />
                          <Text>Se déconnecter</Text>
                        </HStack>
                      </MenuItem>
                    </MenuContent>
                  </MenuPositioner>
                </Portal>
              </MenuRoot>
            </HStack>
          </HStack>
        </Box>

        {/* Page Content */}
        <Box flex={1} p={8} overflowY="auto">
          <Outlet />
        </Box>
      </Flex>
    </Flex>
  )
}
